<template>
    <div class="mt-10 sm:mt-0">
        <div class="hidden sm:block">
            <div class="py-8"><div class="border-t border-gray-200"></div></div>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6 mt-10 sm:mt-0">
            <div class="md:col-span-1 flex justify-between">
                <div class="px-4 sm:px-0">
                    <h3 class="text-lg font-medium text-gray-900">
                        {{ $t("agencies.feeCalculationMode") }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        {{ $t("agencies.feeCalculationModeDescription") }}
                    </p>
                </div>
            </div>

            <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="px-4 py-5 sm:p-6 bg-white shadow sm:rounded-lg">
                    <div class="space-y-3">
                        <label
                            v-for="option in feeCalculation.options"
                            :key="option"
                            class="flex items-start gap-3 text-sm text-gray-700 cursor-pointer select-none"
                            :for="'fee-mode-' + option"
                        >
                            <input
                                :id="'fee-mode-' + option"
                                type="radio"
                                name="fee_calculation_mode"
                                class="mt-1"
                                :value="option"
                                :checked="feeCalculation.mode === option"
                                :disabled="processing"
                                @change="request(option)"
                            />
                            <span>
                                <span class="block font-medium">
                                    {{ $t("agencies.feeCalculationModes." + option) }}
                                    <!-- Entscheid Peter, 31.08.2026: dieselbe Auszeichnung
                                         wie beim Anlegen eines Projekts — sonst wirkte die
                                         Empfehlung, als gälte sie nur dort. -->
                                    <span
                                        v-if="option === 'offer_based'"
                                        class="ml-2 align-middle inline-block px-2 py-0.5 rounded border border-gray-300 bg-white text-[11px] font-medium text-gray-500"
                                    >{{ $t("agencies.feeCalculationModeDefaultBadge") }}</span>
                                </span>
                                <span class="block text-xs text-gray-500">
                                    <span
                                        v-if="option === 'budget_based'"
                                        class="text-gray-600 font-medium"
                                    >{{ $t("agencies.feeCalculationModeExceptionHint") }}</span>
                                    {{ $t("agencies.feeCalculationModeHints." + option) }}
                                </span>
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Entscheid 6 (Auftrag 2026-08-16): Erst die Bestätigung schreibt.
             Der Text nennt die Folge beim Namen — sämtliche Abrechnungen
             dieser Agentur werden neu gerechnet, auch abgeschlossene
             Wohnungen und bereits versandte Exporte. -->
        <jet-dialog-modal :show="pending !== null" @close="cancel">
            <template #title>
                {{ $t("agencies.feeCalculationModeConfirmTitle") }}
            </template>

            <template #content>
                <p class="text-sm text-gray-700">
                    {{ $t("agencies.feeCalculationModeConfirmText") }}
                </p>
                <p v-if="pending" class="mt-4 text-sm font-medium text-gray-900">
                    {{ $t("agencies.feeCalculationModes." + feeCalculation.mode) }}
                    &rarr;
                    {{ $t("agencies.feeCalculationModes." + pending) }}
                </p>
            </template>

            <template #footer>
                <button
                    type="button"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                    @click="cancel"
                >
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button
                    class="ml-3"
                    :class="{ 'opacity-25': processing }"
                    :disabled="processing"
                    @click="confirm"
                >
                    {{ $t("actions.confirm") }}
                </primary-button>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
// Auftrag Luca, 2026-08-16 (Plan
// docs/superpowers/plans/2026-08-16-berechnungsmodi-gebuehr-honorar.md,
// Entscheide 5 und 6): Der Berechnungsmodus ist AUSSCHLIESSLICH für den
// Superadmin sichtbar und änderbar. Die Sichtbarkeit entscheidet der Server:
// `AgencyDetailResource` liefert den `feeCalculation`-Block nur für
// Superadmins, die Seite bindet diese Komponente mit `v-if="feeCalculation"`
// ein — für alle anderen Rollen existiert sie im Markup gar nicht. Die
// schreibende Route trägt zusätzlich `can:superadmin`; Ausblenden allein
// genügt nicht.
//
// Bewusst ein LOKALER Dialog statt einer eigenen Inertia-Modal-Seite: der
// Block ist eine Einstellung auf genau dieser Seite; ein zweiter
// Controller-Endpunkt plus Page nur für die Rückfrage wäre Aufwand ohne
// Gegenwert — und der lokale Dialog ist mit Vitest prüfbar.
import JetDialogModal from "@/Jetstream/DialogModal";
import PrimaryButton from "@/Jetstream/PrimaryButton";

export default {
    name: "FeeCalculationModeCard",
    components: { JetDialogModal, PrimaryButton },
    props: {
        agency: { type: Object, required: true },
        feeCalculation: { type: Object, required: true },
        updateLink: { type: String, required: true },
    },
    data() {
        return {
            // Der angeklickte, noch NICHT geschriebene Modus. `null` heisst:
            // kein Dialog offen.
            pending: null,
            processing: false,
        };
    },
    methods: {
        request(mode) {
            if (mode === this.feeCalculation.mode) {
                return;
            }
            this.pending = mode;
        },
        cancel() {
            // Die Radiogruppe hängt an `feeCalculation.mode` (`:checked`), nicht
            // an einem lokalen Zustand — mit dem Schliessen des Dialogs springt
            // die Auswahl darum von selbst auf den gespeicherten Modus zurück.
            this.pending = null;
            this.$forceUpdate();
        },
        confirm() {
            if (!this.pending || this.processing) {
                return;
            }

            // Die Adresse wird VOR dem Sperren gebildet, und der Wurf wird
            // gefangen (Staging-Befund 2026-08-17). `route()` wirft, wenn
            // Ziggy den Namen nicht kennt — und Ziggy kennt ihn nach einem
            // Deploy so lange nicht, bis der rollenweise Routen-Cache
            // (`ZiggyRouteCacher`, 10 Tage) erneuert ist. Stand `processing =
            // true` davor, blieb der Wurf beim Auswerten des Arguments
            // hängen: Dialog offen, Schaltfläche gesperrt, keine Meldung,
            // keine Anfrage. Für den Benutzer war das «es passiert nichts».
            //
            // Der Fang ist bewusst allgemein und nicht auf Ziggy gemünzt: die
            // Maske darf aus KEINEM Grund in einem gesperrten Zustand ohne
            // Rückmeldung stehenbleiben.
            let url;

            try {
                url = this.route(this.updateLink, this.agency.uuid);
            } catch (error) {
                this.pending = null;
                this.processing = false;
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "actions.defaultError",
                });

                return;
            }

            this.processing = true;
            this.$inertia.put(
                url,
                { mode: this.pending },
                {
                    preserveScroll: true,
                    onSuccess: () =>
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "agencies.feeCalculationModeUpdated",
                        }),
                    // Ohne diesen Zweig schlösse sich der Dialog bei einem
                    // Fehler wortlos (`onFinish` läuft in beiden Fällen) und
                    // der Modus stünde unverändert da — ununterscheidbar von
                    // «hat funktioniert».
                    onError: () =>
                        this.$notify({
                            group: "error",
                            title: "actions.error",
                            text: "actions.defaultError",
                        }),
                    onFinish: () => {
                        this.processing = false;
                        this.pending = null;
                    },
                }
            );
        },
    },
};
</script>

<style scoped></style>
